import React, {useEffect, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSkipMembership,
  setForceLogin,
  setHasMembershipContext,
  setMemberOnlyTicketsDeleted,
  membershipGoToSignIn,
  membershipSignOut,
  setCheckoutOverlay
} from '@checkout/actions/ui';
import { wrappedFetch } from '@checkout-h/fetch';
import { useTranslation } from '@checkout-c/Translations';
import { fetchReservation } from '../actions/reservation';
import { getEventPriceLevels } from 'checkout/actions/event';
import { getEventSeriesChildListing } from '../views/series/actions/event-series';
import { membershipLogoutAndRefresh } from '../helpers/membershipLogoutAndRefresh';
import { selectIsBannerLayout, selectIsPosterLayout} from '@checkout-b/checkout/selectors/ui';
import { navigate } from '@checkout-b/checkout/helpers/navigationService';

const MemberAuthButtons = () => {
  const dispatch = useDispatch();

  const isMembershipAvailable = !!useSelector(state => state.ui?.uiConfig?.membershipFormConfig?.membership_available);
  const isMembershipRequired = !!useSelector(state => state.ui?.uiConfig?.membershipFormConfig?.membership_required);
  const isMembershipSkipped = !!useSelector(state => state.ui?.skipMembership);
  const isMemberLoggedIn = useSelector(state => state.ui.hasMembershipContext);
  const isBannerLayout = useSelector(selectIsBannerLayout);
  const isPosterLayout = useSelector(selectIsPosterLayout);
  const isBannerOrPoster = isBannerLayout || isPosterLayout;
  const isSkipLogin = useSelector(state => state.ui?.uiConfig?.membershipFormConfig?.skip_login);
  const token = useSelector(state => state.reservation?.token);
  const secret = useSelector(state => state.reservation?.secret);
  const selectedChildEvent = useSelector(state => state.event?.childEvent?.event);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const isRecurring = useSelector(state => state.ui.isRecurring);

  useEffect(() => {
    if (!isMembershipAvailable) return;
    const membershipGating = isMembershipRequired || !isSkipLogin;
    if (isBannerOrPoster && membershipGating && !isMemberLoggedIn && (!isMembershipSkipped || isMembershipRequired)) {
      dispatch(membershipGoToSignIn());
      dispatch(setCheckoutOverlay(true));
      dispatch(setSkipMembership(false));
      navigate('/member-login');
    }
  }, [isMembershipAvailable, isBannerOrPoster, isSkipLogin, isMembershipRequired, isMemberLoggedIn, isMembershipSkipped]);

  if (!isMembershipAvailable) {
    return null;
  }

  const login = () => {
    if (isBannerOrPoster) {
      dispatch(membershipGoToSignIn());
      dispatch(setCheckoutOverlay(true));
      dispatch(setSkipMembership(false));
      navigate('/member-login');
      return;
    }
    dispatch(setSkipMembership(false));
    dispatch(setForceLogin(true));
    dispatch(membershipGoToSignIn());
  };

  const logout = async() => {
    if (loading) {
      return;
    }

    setLoading(true);
    try {
      const res = await wrappedFetch(`/api/memberships/${token}/?update_reservation=true`, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'reservation-secret': secret,
        },
      });
      const data = await res.json();
      if (data.deleted) {
        dispatch(setMemberOnlyTicketsDeleted(true));
      }else{
        await dispatch(membershipLogoutAndRefresh(token, secret, isRecurring, selectedChildEvent));
      }
      dispatch(membershipSignOut());
      dispatch(setCheckoutOverlay(false));
      dispatch(setHasMembershipContext(false));
      dispatch(setSkipMembership(false));
    } catch (err) {
      console.error('Logout flow error:', err);
    } finally {
      setLoading(false);
    }
  };

  let linkText = null;
  let onClickHandler = null;

  if (isMemberLoggedIn) {
    linkText = t('checkout__member_logout');
    onClickHandler = logout;
  } else {
    linkText = t('checkout__member_login');
    onClickHandler = login;
  }

  return (
    <div className="member-auth-buttons">
      <a
        href="#"
        style={{ textDecoration: 'none' }}
        onClick={(e) => {
          e.preventDefault();
          if (!loading) {
            onClickHandler();
          }
        }}
      >
        {loading ? <><span> <span className='loader'></span> {linkText}</span></> : linkText}
      </a>
    </div>
  );
};

export default MemberAuthButtons;
